import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  TabMenu,
  TabMenuList,
  TabMenuTrigger,
} from '@ads/components-react';
import {
  ClockIcon,
  EyeIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanEdit } from '../../../context/PlanEditContext';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDateTime';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';
import { PlanContractCard } from '../../organisms/PlanContractCard';

/** Abas da tela de edição de plano. */
export const PLAN_EDIT_TABS = ['detalhes', 'valores', 'documentos'] as const;

/** Aba ativa da tela de edição de plano. */
export type PlanEditTab = (typeof PLAN_EDIT_TABS)[number];

/** Props de `PlanEditTemplate`. */
export interface PlanEditTemplateProps {
  /** Nome do aluno — segmento do breadcrumb. */
  readonly studentName: string;
  /** Curso do contrato, exibido como subtítulo. */
  readonly courseName?: string | null | undefined;
  /** Aba ativa. */
  readonly activeTab: PlanEditTab;
  /** Exibe esqueletos no card do contrato enquanto o editor carrega. */
  readonly isLoading?: boolean | undefined;
  /** Recebe a aba escolhida. */
  readonly onTabChange: (tab: PlanEditTab) => void;
  /** Ações de leitura do cabeçalho. */
  readonly onOrderTracking: () => void;
  readonly onOperationsTracking: () => void;
  readonly onViewParcels: () => void;
  /** Dispara o salvamento. */
  readonly onSave: () => void;
  /** Descarta e volta. */
  readonly onCancel: () => void;
  /** Conteúdo da aba ativa. */
  readonly children: ReactNode;
}

const TAB_LABEL_KEY = {
  detalhes: 'planEdit.tabs.details',
  valores: 'planEdit.tabs.values',
  documentos: 'planEdit.tabs.documents',
} as const satisfies Record<PlanEditTab, string>;

/**
 * Casca da tela de edição de plano.
 *
 * A auditoria e a régua Cancelar/Salvar ficam **fora do painel de abas**, como no
 * legado: são um único `PUT` para as três abas, então as ações não pertencem a
 * nenhuma delas em particular.
 */
export function PlanEditTemplate({
  studentName,
  courseName,
  activeTab,
  isLoading,
  onTabChange,
  onOrderTracking,
  onOperationsTracking,
  onViewParcels,
  onSave,
  onCancel,
  children,
}: PlanEditTemplateProps) {
  const { t } = useTranslation('individualPayment');
  const { editor, isSaving, isReadOnly } = usePlanEdit();

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-mini">
        {/* Só o último segmento é `BreadcrumbPage`. Quem renderiza o ">" é o
            `BreadcrumbLink`, logo um `BreadcrumbPage` no meio — ou o ícone solto
            fora de um link — engole o separador que deveria vir depois dele. */}
        <Breadcrumb>
          <BreadcrumbLink asChild>
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbLink asChild>
            <Link to="/individual-payment">{t('planEdit.breadcrumb.root')}</Link>
          </BreadcrumbLink>
          {/* `asChild={false}` renderiza texto simples e ainda assim emite o
              separador — o aluno é contexto, não um destino navegável daqui. */}
          <BreadcrumbLink asChild={false}>{studentName}</BreadcrumbLink>
          <BreadcrumbPage>{t('planEdit.breadcrumb.current')}</BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-col gap-quark">
          <h1 className="ts-heading-md text-ctx-content-title">{t('planEdit.title')}</h1>
          {courseName ? (
            <p className="ts-paragraph-xs text-ctx-content-placeholder">{courseName}</p>
          ) : null}
        </div>
      </div>

      <PlanContractCard
        editor={editor}
        isLoading={isLoading}
        actions={
          <span className="flex flex-wrap items-center gap-micro">
            <Button
              hierarchy="secondary"
              size="sm"
              leadingIcon={MagnifyingGlassIcon}
              onClick={onOrderTracking}
            >
              {t('planEdit.headerActions.orderTracking')}
            </Button>
            <Button
              hierarchy="secondary"
              size="sm"
              leadingIcon={ClockIcon}
              onClick={onOperationsTracking}
            >
              {t('planEdit.headerActions.operationsTracking')}
            </Button>
            <Button hierarchy="secondary" size="sm" leadingIcon={EyeIcon} onClick={onViewParcels}>
              {t('planEdit.headerActions.viewParcels')}
            </Button>
          </span>
        }
      />

      <TabMenu value={activeTab} onValueChange={value => onTabChange(value as PlanEditTab)}>
        <TabMenuList>
          {PLAN_EDIT_TABS.map(tab => (
            <TabMenuTrigger key={tab} value={tab}>
              {t(TAB_LABEL_KEY[tab])}
            </TabMenuTrigger>
          ))}
        </TabMenuList>
      </TabMenu>

      {children}

      <PlanSectionCard
        title={t('planEdit.audit.title')}
        icon={InformationCircleIcon}
        badge={t('planEdit.audit.readOnly')}
      >
        <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
          <PlanReadOnlyField
            label={t('planEdit.audit.createdBy')}
            value={editor?.userNameCreator ?? EMPTY_VALUE}
          />
          <PlanReadOnlyField
            label={t('planEdit.audit.createdAt')}
            value={formatDateTime(editor?.creationDate)}
          />
          <span />
        </div>
        <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
          <PlanReadOnlyField
            label={t('planEdit.audit.updatedBy')}
            value={editor?.userNameLastChange ?? EMPTY_VALUE}
          />
          <PlanReadOnlyField
            label={t('planEdit.audit.updatedAt')}
            value={formatDateTime(editor?.lastChangeDate)}
          />
          <span />
        </div>
      </PlanSectionCard>

      <div className="flex flex-wrap items-center justify-end gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-mini">
        <span className="flex items-center gap-micro">
          <Button hierarchy="secondary" size="md" disabled={isSaving} onClick={onCancel}>
            {t('planEdit.footer.cancel')}
          </Button>
          <Button
            hierarchy="primary"
            size="md"
            isLoading={isSaving}
            disabled={isSaving || isReadOnly}
            onClick={onSave}
          >
            {isSaving ? t('planEdit.footer.saving') : t('planEdit.footer.save')}
          </Button>
        </span>
      </div>
    </div>
  );
}
